/* Aevex Accessibility Enhancements — focus, keyboard, ARIA, motion, SR */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* 1. ARIA LIVE REGION — persistent announcer for screen readers       */
  /* ------------------------------------------------------------------ */
  var liveRegion = null;

  function createLiveRegion() {
    if (liveRegion) return liveRegion;
    liveRegion = document.createElement('div');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.setAttribute('role', 'status');
    liveRegion.className = 'visually-hidden';
    liveRegion.id = 'aevex-a11y-announcer';
    document.body.appendChild(liveRegion);
    return liveRegion;
  }

  function announce(message, priority) {
    var region = createLiveRegion();
    if (priority === 'assertive') {
      region.setAttribute('aria-live', 'assertive');
    } else {
      region.setAttribute('aria-live', 'polite');
    }
    // Force reflow so SR picks up the change
    region.textContent = '';
    requestAnimationFrame(function () {
      region.textContent = message;
    });
    // Reset to polite after 5s
    if (priority === 'assertive') {
      setTimeout(function () {
        region.setAttribute('aria-live', 'polite');
      }, 5000);
    }
  }

  window.aevexAnnounce = announce;

  /* ------------------------------------------------------------------ */
  /* 2. SKIP NAVIGATION LINK                                            */
  /* ------------------------------------------------------------------ */
  function initSkipNav() {
    var skipLink = document.querySelector('.skip-to-content-link');
    if (!skipLink) return;

    skipLink.addEventListener('click', function (e) {
      e.preventDefault();
      var target = document.getElementById('MainContent');
      if (target) {
        target.setAttribute('tabindex', '-1');
        target.focus();
        target.addEventListener('blur', function handler() {
          target.removeAttribute('tabindex');
          target.removeEventListener('blur', handler);
        });
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* 3. FOCUS TRAP for modals, drawers, dropdowns                      */
  /* ------------------------------------------------------------------ */
  var focusTrapStack = [];

  function getFocusableElements(container) {
    return Array.from(container.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), ' +
      'input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ));
  }

  function trapFocus(container, options) {
    options = options || {};
    var firstFocusable = options.firstFocusable || null;

    function handleKeydown(event) {
      if (event.key !== 'Tab') return;
      var focusable = getFocusableElements(container);
      if (focusable.length === 0) { event.preventDefault(); return; }

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    container.addEventListener('keydown', handleKeydown);
    focusTrapStack.push({ container: container, handler: handleKeydown });

    if (firstFocusable) {
      requestAnimationFrame(function () { firstFocusable.focus(); });
    } else {
      var first = getFocusableElements(container)[0];
      if (first) requestAnimationFrame(function () { first.focus(); });
    }
  }

  function releaseFocusTrap(container) {
    var idx = focusTrapStack.findIndex(function (t) { return t.container === container; });
    if (idx === -1) return;
    var trap = focusTrapStack[idx];
    container.removeEventListener('keydown', trap.handler);
    focusTrapStack.splice(idx, 1);
  }

  /* ------------------------------------------------------------------ */
  /* 4. CART DRAWER FOCUS TRAP                                          */
  /* ------------------------------------------------------------------ */
  function observeCartDrawer() {
    var cartShell = document.querySelector('[data-cart-shell]');
    if (!cartShell) return;

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.attributeName === 'class') {
          if (cartShell.classList.contains('is-open')) {
            var drawer = cartShell.querySelector('[data-cart-drawer]');
            if (drawer) trapFocus(drawer);
          } else {
            var drawer = cartShell.querySelector('[data-cart-drawer]');
            if (drawer) releaseFocusTrap(drawer);
          }
        }
      });
    });
    observer.observe(cartShell, { attributes: true, attributeFilter: ['class'] });
  }

  /* ------------------------------------------------------------------ */
  /* 5. COUNTRY SELECTOR FOCUS TRAP                                     */
  /* ------------------------------------------------------------------ */
  function observeCountrySelector() {
    var countryToggle = document.querySelector('[data-country-toggle]');
    var countryDropdown = document.querySelector('[data-country-dropdown]');
    if (!countryToggle || !countryDropdown) return;

    countryToggle.addEventListener('click', function () {
      var isOpen = countryToggle.getAttribute('aria-expanded') === 'true';
      if (!isOpen) {
        trapFocus(countryDropdown, { firstFocusable: countryDropdown.querySelector('input, a, button') });
      } else {
        releaseFocusTrap(countryDropdown);
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && countryToggle.getAttribute('aria-expanded') === 'true') {
        countryToggle.click();
        countryToggle.focus();
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* 6. KEYBOARD NAVIGATION — arrow keys for menus, carousel controls   */
  /* ------------------------------------------------------------------ */
  function initKeyboardNav() {
    // Arrow key navigation for horizontal menus
    document.addEventListener('keydown', function (event) {
      var target = event.target;
      if (!target) return;

      // Dropdown menus
      if (target.matches('[role="menuitem"]')) {
        var menu = target.closest('[role="menu"]');
        if (!menu) return;
        var items = Array.from(menu.querySelectorAll('[role="menuitem"]'));
        var idx = items.indexOf(target);

        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
          event.preventDefault();
          var next = items[(idx + 1) % items.length];
          next.focus();
        } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
          event.preventDefault();
          var prev = items[(idx - 1 + items.length) % items.length];
          prev.focus();
        } else if (event.key === 'Home') {
          event.preventDefault();
          items[0].focus();
        } else if (event.key === 'End') {
          event.preventDefault();
          items[items.length - 1].focus();
        }
      }

      // Carousel/slideshow controls
      if (target.matches('[data-carousel-btn]')) {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          var group = target.closest('[data-carousel-group]');
          if (!group) return;
          var btns = Array.from(group.querySelectorAll('[data-carousel-btn]'));
          var i = btns.indexOf(target);
          if (event.key === 'ArrowRight') {
            btns[(i + 1) % btns.length].focus();
          } else {
            btns[(i - 1 + btns.length) % btns.length].focus();
          }
        }
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* 7. SCREEN READER ANNOUNCEMENTS — cart updates                      */
  /* ------------------------------------------------------------------ */
  function observeCartUpdates() {
    document.addEventListener('click', function (event) {
      var addBtn = event.target.closest('[name="add"], [data-add-to-cart]');
      if (addBtn) {
        announce('Adding item to cart…');
      }

      var removeBtn = event.target.closest('[data-cart-change]');
      if (removeBtn) {
        var qty = parseInt(removeBtn.dataset.quantity, 10);
        if (qty === 0) {
          announce('Removing item from cart');
        } else {
          announce('Updating cart quantity');
        }
      }
    });

    // After cart refresh, announce new count
    var origRefresh = window.refreshCart;
    if (typeof origRefresh === 'function') {
      window.refreshCart = function () {
        return origRefresh.apply(this, arguments).then(function (cart) {
          if (cart) {
            announce('Cart updated. ' + cart.item_count + ' item' + (cart.item_count !== 1 ? 's' : '') + ' in cart.');
          }
        });
      };
    }
  }

  /* ------------------------------------------------------------------ */
  /* 8. FORM ERROR ANNOUNCEMENTS                                        */
  /* ------------------------------------------------------------------ */
  function observeFormErrors() {
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || form.tagName !== 'FORM') return;

      // Check for HTML5 validation failures
      setTimeout(function () {
        var invalid = form.querySelector(':invalid:not(form)');
        if (invalid) {
          var label = invalid.labels && invalid.labels[0]
            ? invalid.labels[0].textContent.trim()
            : invalid.getAttribute('aria-label') || invalid.name || 'Field';
          var error = invalid.validationMessage || 'Please correct this field';
          announce('Error in ' + label + ': ' + error, 'assertive');
          invalid.focus();
        }
      }, 100);
    });
  }

  /* ------------------------------------------------------------------ */
  /* 9. REDUCED MOTION SUPPORT                                          */
  /* ------------------------------------------------------------------ */
  function respectReducedMotion() {
    var mq = window.matchMedia('(prefers-reduced-motion: reduce)');

    function apply(reduced) {
      document.documentElement.classList.toggle('reduced-motion', reduced);
      if (reduced) {
        // Disable CSS animations
        var style = document.createElement('style');
        style.id = 'aevex-reduced-motion';
        style.textContent = '*, *::before, *::after { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important; }';
        document.head.appendChild(style);
      } else {
        var existing = document.getElementById('aevex-reduced-motion');
        if (existing) existing.remove();
      }
    }

    apply(mq.matches);
    mq.addEventListener('change', function (e) { apply(e.matches); });
  }

  /* ------------------------------------------------------------------ */
  /* 10. HIGH CONTRAST MODE                                             */
  /* ------------------------------------------------------------------ */
  function observeHighContrast() {
    var mq = window.matchMedia('(forced-colors: active)');
    function apply(active) {
      document.documentElement.classList.toggle('high-contrast', active);
    }
    apply(mq.matches);
    mq.addEventListener('change', function (e) { apply(e.matches); });
  }

  /* ------------------------------------------------------------------ */
  /* 11. FOCUS VISIBLE MANAGEMENT                                        */
  /* ------------------------------------------------------------------ */
  function initFocusVisible() {
    // Only show focus rings for keyboard users
    document.body.addEventListener('mousedown', function () {
      document.documentElement.classList.add('using-mouse');
    });
    document.body.addEventListener('keydown', function (e) {
      if (e.key === 'Tab') {
        document.documentElement.classList.remove('using-mouse');
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* INIT                                                                */
  /* ------------------------------------------------------------------ */
  document.addEventListener('DOMContentLoaded', function () {
    createLiveRegion();
    initSkipNav();
    observeCartDrawer();
    observeCountrySelector();
    initKeyboardNav();
    observeCartUpdates();
    observeFormErrors();
    respectReducedMotion();
    observeHighContrast();
    initFocusVisible();
  });
})();
